import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
};

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, loginWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const user = await login(email, password);
      // Redirect based on role
      switch (user.role) {
        case 'super_admin':
        case 'admin':
          navigate('/admin');
          break;
        case 'curator':
          navigate('/curator');
          break;
        case 'artist':
          navigate('/artist');
          break;
        case 'model':
          navigate('/model');
          break;
        case 'venue_owner':
          navigate('/venue');
          break;
        default:
          navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout hideFooter>
      <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4" data-testid="login-page">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <span className="font-accent text-2xl tracking-[0.15em] text-white">
                GS <span className="text-gold-gradient">AGENCY</span>
              </span>
            </Link>
            <h1 className="font-display text-3xl text-white mb-2">
              {t('auth.login')}
            </h1>
            <p className="text-white/50">
              Accede a tu cuenta para gestionar tu perfil
            </p>
          </div>

          <div className="premium-card p-8">
            {/* Google Login */}
            <button
              onClick={loginWithGoogle}
              className="w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition-colors mb-6"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-white">{t('auth.google_login')}</span>
            </button>

            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/40 text-sm">{t('auth.or')}</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* Email Login Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-white/60 text-sm mb-2">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="input-premium w-full pl-12"
                    placeholder="tu@email.com"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-white/60 text-sm mb-2">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="input-premium w-full pl-12 pr-12"
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {error && (
                <p className="text-red-500 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-gold w-full"
                data-testid="login-submit"
              >
                {loading ? 'Cargando...' : t('auth.login')}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/forgot-password" className="text-white/50 text-sm hover:text-white transition-colors">
                {t('auth.forgot_password')}
              </Link>
            </div>
          </div>

          <p className="text-center text-white/50 mt-6">
            {t('auth.no_account')}{' '}
            <Link to="/register" className="text-[#D4AF37] hover:underline">
              {t('auth.register')}
            </Link>
          </p>
        </motion.div>
      </div>
    </Layout>
  );
}
